// src/components/payment/MonerisPaymentForm.js
// Formulaire de paiement Moneris avec Hosted Tokenization
// PCI Compliant - Les données de carte ne passent jamais par nos serveurs

import React, { useState, useEffect, useRef, useCallback } from "react";
import { getActiveConfig } from "../../config/moneris";
import {
  CreditCard,
  Shield,
  Loader2,
  CheckCircle,
  AlertCircle,
  Lock,
  RefreshCw,
} from "lucide-react";

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const MonerisPaymentForm = ({
  onTokenReceived,
  onError,
  amount,
  planName,
  disabled = false,
}) => {
  const config = getActiveConfig();
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tokenReceived, setTokenReceived] = useState(false);
  const [error, setError] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);

  // ============================================
  // CONSTRUIRE L'URL DE L'IFRAME
  // ============================================

  const buildIframeUrl = useCallback(() => {
    const params = new URLSearchParams({
      id: config.hostedTokenizationProfileId,
      pmmsg: "true",
      display_labels: "1",
      enable_exp: "1",
      enable_cvd: "1",
      css_body: "background-color:transparent;",
      css_textbox:
        "border:1px solid #475569;border-radius:8px;padding:12px;background-color:#1e293b;color:#fff;font-size:16px;width:100%;margin-bottom:12px;",
      css_label:
        "color:#cbd5e1;font-size:14px;margin-bottom:4px;display:block;",
    });

    return `${config.hostedTokenizationUrl}?${params.toString()}`;
  }, [config]);

  // ============================================
  // ÉCOUTER LES MESSAGES DE L'IFRAME
  // ============================================

  useEffect(() => {
    const handleMessage = (event) => {
      // Vérifier l'origine (Moneris)
      if (!event.origin.includes("moneris.com")) {
        return;
      }

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        console.log("Moneris response:", data);

        // Token reçu avec succès (dataKey présent = succès)
        if (data.dataKey) {
          setTokenReceived(true);
          setIsProcessing(false);
          setError(null);

          if (onTokenReceived) {
            onTokenReceived({
              dataKey: data.dataKey,
              responseCode: Array.isArray(data.responseCode)
                ? data.responseCode[0]
                : data.responseCode,
              bin: data.bin,
              lastFour:
                data.lastFour || (data.bin ? data.bin.slice(-4) : "****"),
              cardType: data.cardType || detectCardType(data.bin),
            });
          }
          return; // Arrêter ici - c'est un succès!
        }

        // Erreur seulement si PAS de dataKey ET message d'erreur
        if (data.errorMessage && !data.dataKey) {
          const errorMsg = translateError(data.errorMessage);
          setError(errorMsg);
          setIsProcessing(false);

          if (onError) {
            onError(errorMsg);
          }
          return;
        }

        // Response code d'erreur (sans dataKey)
        const respCode = Array.isArray(data.responseCode)
          ? data.responseCode[0]
          : data.responseCode;

        if (respCode && respCode !== "001" && !data.dataKey) {
          const errorMsg = getResponseCodeMessage(respCode);
          setError(errorMsg);
          setIsProcessing(false);

          if (onError) {
            onError(errorMsg);
          }
        }
      } catch (e) {
        console.error("Error parsing Moneris message:", e);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onTokenReceived, onError]);

  // ============================================
  // DÉTECTER LE TYPE DE CARTE
  // ============================================

  const detectCardType = (bin) => {
    if (!bin) return "card";
    if (bin.startsWith("4")) return "visa";
    if (bin.startsWith("5") || bin.startsWith("2")) return "mastercard";
    if (bin.startsWith("3")) return "amex";
    return "card";
  };

  // ============================================
  // TRADUIRE LES ERREURS MONERIS
  // ============================================

  const translateError = (errorMsg) => {
    const translations = {
      "Invalid card number": "Numéro de carte invalide",
      "Invalid expiry date": "Date d'expiration invalide",
      "Invalid CVD": "Code de sécurité (CVV) invalide",
      "Card declined": "Carte refusée",
      "Expired card": "Carte expirée",
      "Insufficient funds": "Fonds insuffisants",
      "invalid data": "Données invalides - vérifiez les informations",
    };
    return translations[errorMsg] || errorMsg;
  };

  const getResponseCodeMessage = (code) => {
    const messages = {
      "001": "Succès", // Ce n'est pas une erreur!
      "050": "Carte refusée",
      "051": "Carte expirée",
      "052": "Code PIN incorrect",
      "053": "Carte non acceptée",
      "054": "Problème avec la carte",
      "055": "Fonds insuffisants",
      940: "Profile ID invalide",
      941: "Erreur de génération du token",
      942: "Credentials invalides",
      943: "Données de carte invalides",
      944: "Erreur de configuration du profil",
      945: "CVD invalide",
    };
    return messages[code] || `Erreur de paiement (code: ${code})`;
  };

  // ============================================
  // GESTION DE L'IFRAME
  // ============================================

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError("Erreur de chargement du formulaire de paiement");
  };

  const reloadIframe = () => {
    setIframeKey((prev) => prev + 1);
    setIsLoading(true);
    setError(null);
    setTokenReceived(false);
  };

  // ============================================
  // SOUMETTRE LE FORMULAIRE (déclencher tokenization)
  // ============================================

  const submitPayment = () => {
    if (disabled || isProcessing || tokenReceived) return;

    setIsProcessing(true);
    setError(null);

    // Envoyer un message à l'iframe pour déclencher la tokenization
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage("tokenize", "*");
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-300">
          <CreditCard className="w-5 h-5" />
          <span className="font-medium">Carte de crédit</span>
        </div>
        <div className="flex items-center gap-2 text-green-400 text-sm">
          <Lock className="w-4 h-4" />
          <span>Sécurisé par Moneris</span>
        </div>
      </div>

      {/* Iframe Container */}
      <div
        className={`relative rounded-xl border overflow-hidden transition-all ${
          tokenReceived
            ? "border-green-500 bg-green-500/10"
            : error
            ? "border-red-500 bg-red-500/10"
            : "border-slate-600 bg-slate-800"
        }`}
        style={{ minHeight: "280px" }}
      >
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
              <span className="text-slate-400 text-sm">
                Chargement du formulaire sécurisé...
              </span>
            </div>
          </div>
        )}

        {/* Success overlay */}
        {tokenReceived && (
          <div className="absolute inset-0 flex items-center justify-center bg-green-900/90 z-10">
            <div className="flex flex-col items-center gap-3">
              <CheckCircle className="w-12 h-12 text-green-400" />
              <span className="text-green-300 font-medium">
                Carte validée avec succès!
              </span>
            </div>
          </div>
        )}

        {/* Moneris Iframe */}
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={buildIframeUrl()}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Moneris Hosted Tokenization"
          className="w-full h-[280px] border-0"
          style={{ backgroundColor: "transparent" }}
          sandbox="allow-scripts allow-same-origin allow-forms"
        />
      </div>

      {/* Error message */}
      {error && !tokenReceived && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={reloadIframe}
            className="ml-auto flex items-center gap-1 text-sm hover:text-red-300"
          >
            <RefreshCw className="w-4 h-4" />
            Réessayer
          </button>
        </div>
      )}

      {/* Cartes de test (sandbox) */}
      {config.environment === "sandbox" && !tokenReceived && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-amber-400 text-xs font-medium mb-1">
            🧪 Mode Test - Cartes de test:
          </p>
          <div className="text-amber-300 text-xs space-y-1">
            <p>
              ✅ Visa: <code>4242 4242 4242 4242</code>
            </p>
            <p>
              ✅ MC: <code>5454 5454 5454 5454</code>
            </p>
            <p>Expiration: 1228 | CVV: 123</p>
          </div>
        </div>
      )}

      {/* Bouton de validation */}
      {!tokenReceived && (
        <button
          onClick={submitPayment}
          disabled={disabled || isLoading || isProcessing}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-all ${
            disabled || isLoading || isProcessing
              ? "bg-slate-700 text-slate-500 cursor-not-allowed"
              : "bg-sky-500 text-white hover:bg-sky-600"
          }`}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Validation en cours...
            </>
          ) : (
            <>
              <Shield className="w-5 h-5" />
              Valider la carte
            </>
          )}
        </button>
      )}

      {/* Récap montant */}
      {amount && (
        <div className="text-center text-sm text-slate-400">
          <span>
            {planName && <span className="text-white">{planName}</span>} —{" "}
          </span>
          <span className="text-white font-semibold">{amount}$ CAD</span>
          <span> après l'essai gratuit de 14 jours</span>
        </div>
      )}

      {/* Footer sécurité */}
      <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <Lock className="w-3 h-3" />
          Chiffrement SSL
        </span>
        <span>•</span>
        <span>Conforme PCI DSS</span>
        <span>•</span>
        <span>🇨🇦 Moneris Canada</span>
      </div>
    </div>
  );
};

export default MonerisPaymentForm;
